import { Inject, Injectable } from '@angular/core';
import idb, { DB, UpgradeDB } from 'idb';
import { INDEXEDDB_TOKEN } from './indexedDB-token';
import { InternalContext } from './internal-context';

@Injectable({
  providedIn: 'root',
})
export class InternalDatabaseService {
  private databaseName: string = 'StockCare_Data';
  private databaseVersion: number = 58;
  private database: DB;

  private hasBeenInitialise: boolean = false;

  constructor(@Inject(INDEXEDDB_TOKEN) private indexedDB: IDBFactory, private internalContext: InternalContext) {}

  public async add<T>(tableName: string, data: T): Promise<IDBValidKey> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);

    return await objectStore.add(data);
  }

  public async addRange<T>(tableName: string, data: T[]) {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);
    for (const item of data) {
      await objectStore.add(item);
    }
  }

  public async update<T>(tableName: string, data: T): Promise<IDBValidKey> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);

    return await objectStore.put(data);
  }

  public async delete(tableName: string, primaryKey: any): Promise<void> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);

    await objectStore.delete(primaryKey);
  }

  public async deleteRange<T>(tableName: string, primaryKeys: string[]): Promise<void> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);

    for (const primaryKey of primaryKeys) {
      await objectStore.delete(primaryKeys);
    }
  }

  public async deleteAll(tableName: string): Promise<void> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readwrite');
    const objectStore = transaction.objectStore(tableName);

    await objectStore.clear();
  }

  public async getByPrimaryKey<T>(tableName: string, value: any): Promise<T> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);

    return await objectStore.get(value);
  }

  public async getByIndex<T>(tableName: string, indexName: string, value: any): Promise<T> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);
    const index = objectStore.index(indexName);

    return await index.get(value);
  }

  public async getAllByIndex<T>(tableName: string, indexName: string, value: any): Promise<T[]> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);
    const index = objectStore.index(indexName);

    return await index.getAll(value);
  }

  public async getAll<T>(tableName: string): Promise<T[]> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);

    return await objectStore.getAll();
  }

  public async getAllKeys(tableName: string): Promise<any[]> {
    await this.initialise();

    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);

    return await objectStore.getAllKeys();
  }

  public async getByRange<T>(tableName: string, range: IDBKeyRange): Promise<T[]> {
    const list: T[] = [];
    const transaction = this.database.transaction(tableName, 'readonly');
    const objectStore = transaction.objectStore(tableName);

    let console = await objectStore.openCursor();
    while (true) {
      list.push(console.value);
      console = await console.continue();
      if (!console || list.length > 20) break;
    }
    return list;
  }

  public async deleteDatabase(): Promise<void> {
    await idb.delete(this.databaseName);
  }

  public async initialiseDatabase(): Promise<void> {
    await this.initialise();
  }

  private async initialise(): Promise<void> {
    if (this.hasBeenInitialise) {
      return;
    }

    if (!this.indexedDB) {
      throw new Error('IndexDB not supported');
    }

    this.database = await idb.open(this.databaseName, this.databaseVersion, (upgradeDB) => this.createDatabase(upgradeDB));
    this.hasBeenInitialise = true;
  }

  private createDatabase(localDatabase: UpgradeDB): void {
    for (const table of this.internalContext.getAllTableDefinitions()) {
      const doesTableAlreadyExist = localDatabase.objectStoreNames.contains(table.TableName);

      if (!doesTableAlreadyExist) {
        localDatabase.createObjectStore(table.TableName, table.getIDBObjectStoreParameters());

        if (!table.Indexes || table.Indexes.length === 0) {
          continue;
        }
      }

      const objectStore = localDatabase.transaction.objectStore(table.TableName);
      for (const index of table.Indexes) {
        if (!objectStore.indexNames.contains(index.Name)) {
          objectStore.createIndex(index.Name, index.Key, index.getIDBIndexParameters());
        }
      }
    }
  }
}
